import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Sparkles, Check, Trash2, ShoppingCart, Megaphone, Users, ChevronDown, Loader2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLocale } from "@/context/AppContext";
import { money, pct } from "@/lib/format";
import { RiskBadge } from "@/components/shared/Controls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { CURRENCIES } from "@/lib/i18n";

const EMPTY = {
  product: "", category: "", origin_country: "", sale_country: "", currency: "BRL",
  purchase_price: "", shipping: "", fees: "", taxes: "", commission: "", other_costs: "", sale_price: "", notes: "",
};

const STATUS_LABEL = { new: "Nova", analyzed: "Analisada", approved: "Aprovada", discarded: "Descartada", purchased: "Comprada" };

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}

export default function Opportunities({ refreshAlerts }) {
  const { locale } = useLocale();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [extra, setExtra] = useState({}); // offer / customers per opp

  const load = async () => {
    const { data } = await api.get("/opportunities");
    setItems(data.opportunities);
  };
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ["purchase_price", "shipping", "fees", "taxes", "commission", "other_costs", "sale_price"].forEach((k) => { payload[k] = parseFloat(form[k]) || 0; });
      await api.post("/opportunities", payload);
      toast.success("Oportunidade adicionada");
      setForm(EMPTY); setOpen(false);
      load(); refreshAlerts?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const mark = (id, key, val) => setBusy((b) => ({ ...b, [`${id}_${key}`]: val }));

  const analyze = async (id) => {
    mark(id, "analyze", true);
    try {
      await api.post(`/opportunities/${id}/analyze`);
      toast.success("Análise concluída pela IA");
      setExpanded(id); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { mark(id, "analyze", false); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/opportunities/${id}/status`, { status }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const makeOffer = async (id) => {
    mark(id, "offer", true);
    try {
      const { data } = await api.post(`/opportunities/${id}/offer`, { locale });
      setExtra((e) => ({ ...e, [id]: { ...(e[id] || {}), offer: data.offer } }));
      setExpanded(id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { mark(id, "offer", false); }
  };

  const findCustomers = async (id) => {
    mark(id, "cust", true);
    try {
      const { data } = await api.post(`/opportunities/${id}/find-customers`, { locale });
      setExtra((e) => ({ ...e, [id]: { ...(e[id] || {}), customers: data.result } }));
      setExpanded(id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { mark(id, "cust", false); }
  };

  return (
    <div className="space-y-6 animate-float-up">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">Adicione oportunidades manualmente. A IA calcula custo, margem e ROI e analisa risco e mercado — sem inventar dados.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-opportunity-btn"><Plus className="h-4 w-4 mr-1" /> Nova oportunidade</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Nova oportunidade</DialogTitle></DialogHeader>
            <form onSubmit={create} className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Field label="Produto"><Input required value={form.product} onChange={(e) => setF("product", e.target.value)} data-testid="opp-product" /></Field></div>
              <Field label="Categoria"><Input value={form.category} onChange={(e) => setF("category", e.target.value)} data-testid="opp-category" /></Field>
              <Field label="Moeda">
                <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.currency} onChange={(e) => setF("currency", e.target.value)} data-testid="opp-currency">
                  {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
              </Field>
              <Field label="País de origem"><Input value={form.origin_country} onChange={(e) => setF("origin_country", e.target.value)} placeholder="Ex: CN" data-testid="opp-origin" /></Field>
              <Field label="País de venda"><Input value={form.sale_country} onChange={(e) => setF("sale_country", e.target.value)} placeholder="Ex: BR" data-testid="opp-sale-country" /></Field>
              <Field label="Preço de compra"><Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => setF("purchase_price", e.target.value)} data-testid="opp-purchase" /></Field>
              <Field label="Frete"><Input type="number" step="0.01" value={form.shipping} onChange={(e) => setF("shipping", e.target.value)} /></Field>
              <Field label="Taxas"><Input type="number" step="0.01" value={form.fees} onChange={(e) => setF("fees", e.target.value)} /></Field>
              <Field label="Impostos estimados"><Input type="number" step="0.01" value={form.taxes} onChange={(e) => setF("taxes", e.target.value)} /></Field>
              <Field label="Comissão"><Input type="number" step="0.01" value={form.commission} onChange={(e) => setF("commission", e.target.value)} /></Field>
              <Field label="Outros custos"><Input type="number" step="0.01" value={form.other_costs} onChange={(e) => setF("other_costs", e.target.value)} /></Field>
              <Field label="Preço de venda potencial"><Input type="number" step="0.01" value={form.sale_price} onChange={(e) => setF("sale_price", e.target.value)} data-testid="opp-sale-price" /></Field>
              <div className="sm:col-span-2"><Field label="Observações"><Textarea value={form.notes} onChange={(e) => setF("notes", e.target.value)} rows={2} /></Field></div>
              <DialogFooter className="sm:col-span-2">
                <Button type="submit" disabled={saving} data-testid="opp-submit">{saving ? "Salvando…" : "Salvar oportunidade"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">
          Nenhuma oportunidade ainda. Clique em <span className="text-foreground font-medium">Nova oportunidade</span> para começar.
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((o) => (
            <div key={o.opportunity_id} className="rounded-xl border border-border bg-card p-5" data-testid={`opp-card-${o.opportunity_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-semibold text-lg">{o.product}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{STATUS_LABEL[o.status]}</span>
                    {o.risk_level && <RiskBadge level={o.risk_level} />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{o.category || "—"} · {o.origin_country || "?"} → {o.sale_country || "?"} · {o.currency}</p>
                </div>
                {o.opportunity_score != null && (
                  <div className="text-right">
                    <p className="text-[10px] uppercase font-mono text-muted-foreground">Score</p>
                    <p className="font-heading text-2xl font-bold text-primary">{o.opportunity_score}</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
                <div><p className="text-xs text-muted-foreground">Custo total</p><p className="font-mono font-semibold">{money(o.cost_total, o.currency)}</p></div>
                <div><p className="text-xs text-muted-foreground">Preço venda</p><p className="font-mono font-semibold">{money(o.sale_price, o.currency)}</p></div>
                <div><p className="text-xs text-muted-foreground">Lucro est.</p><p className={`font-mono font-semibold ${o.profit_est >= 0 ? "text-emerald-500" : "text-red-500"}`}>{money(o.profit_est, o.currency)}</p></div>
                <div><p className="text-xs text-muted-foreground">Margem / ROI</p><p className="font-mono font-semibold">{pct(o.margin_pct)} / {pct(o.roi_pct)}</p></div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" variant="secondary" onClick={() => analyze(o.opportunity_id)} disabled={busy[`${o.opportunity_id}_analyze`]} data-testid={`analyze-${o.opportunity_id}`}>
                  {busy[`${o.opportunity_id}_analyze`] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Analisar
                </Button>
                <Button size="sm" variant="outline" onClick={() => setStatus(o.opportunity_id, "approved")} data-testid={`approve-${o.opportunity_id}`}><Check className="h-3.5 w-3.5" /> Salvar</Button>
                <Button size="sm" variant="outline" onClick={() => setStatus(o.opportunity_id, "purchased")}><ShoppingCart className="h-3.5 w-3.5" /> Comprar</Button>
                <Button size="sm" variant="outline" onClick={() => makeOffer(o.opportunity_id)} disabled={busy[`${o.opportunity_id}_offer`]}>
                  {busy[`${o.opportunity_id}_offer`] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Megaphone className="h-3.5 w-3.5" />} Criar oferta
                </Button>
                <Button size="sm" variant="outline" onClick={() => findCustomers(o.opportunity_id)} disabled={busy[`${o.opportunity_id}_cust`]}>
                  {busy[`${o.opportunity_id}_cust`] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />} Encontrar clientes
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setStatus(o.opportunity_id, "discarded")} data-testid={`discard-${o.opportunity_id}`}><Trash2 className="h-3.5 w-3.5" /> Descartar</Button>
                {(o.analysis || extra[o.opportunity_id]) && (
                  <Button size="sm" variant="ghost" onClick={() => setExpanded(expanded === o.opportunity_id ? null : o.opportunity_id)}>
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded === o.opportunity_id ? "rotate-180" : ""}`} /> Detalhes
                  </Button>
                )}
              </div>

              {expanded === o.opportunity_id && (
                <div className="mt-4 pt-4 border-t border-border space-y-4 text-sm">
                  {o.analysis && (
                    <>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <div><p className="text-xs text-muted-foreground">Demanda</p><p>{o.analysis.demand}</p></div>
                        <div><p className="text-xs text-muted-foreground">Concorrência</p><p>{o.analysis.competition}</p></div>
                        <div><p className="text-xs text-muted-foreground">Público-alvo</p><p>{o.analysis.target_audience}</p></div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Por que este score</p>
                        <p className="text-muted-foreground">{o.analysis.score_explanation}</p>
                      </div>
                      {o.analysis.risk_factors?.length > 0 && (
                        <div><p className="text-xs text-muted-foreground mb-1">Fatores de risco</p><ul className="list-disc pl-5 text-muted-foreground">{o.analysis.risk_factors.map((r, i) => <li key={i}>{r}</li>)}</ul></div>
                      )}
                      <div className="grid sm:grid-cols-3 gap-3">
                        <div className="rounded-lg bg-emerald-500/10 p-3"><p className="text-xs font-mono text-emerald-500 mb-1">DADOS CONFIRMADOS</p>{(o.analysis.data_confirmed?.length ? o.analysis.data_confirmed : ["Dado não disponível"]).map((x, i) => <p key={i} className="text-xs">{x}</p>)}</div>
                        <div className="rounded-lg bg-amber-500/10 p-3"><p className="text-xs font-mono text-amber-500 mb-1">ESTIMATIVAS</p>{(o.analysis.estimates?.length ? o.analysis.estimates : ["Dado não disponível"]).map((x, i) => <p key={i} className="text-xs">{x}</p>)}</div>
                        <div className="rounded-lg bg-blue-500/10 p-3"><p className="text-xs font-mono text-blue-500 mb-1">HIPÓTESES</p>{(o.analysis.hypotheses?.length ? o.analysis.hypotheses : ["Dado não disponível"]).map((x, i) => <p key={i} className="text-xs">{x}</p>)}</div>
                      </div>
                    </>
                  )}
                  {extra[o.opportunity_id]?.offer && (
                    <div><p className="text-xs font-mono text-primary mb-1">OFERTA (SALES AI)</p><pre className="whitespace-pre-wrap font-sans text-muted-foreground bg-secondary/40 rounded-lg p-3">{extra[o.opportunity_id].offer}</pre></div>
                  )}
                  {extra[o.opportunity_id]?.customers && (
                    <div><p className="text-xs font-mono text-primary mb-1">CLIENTES (CUSTOMER FINDER AI)</p><pre className="whitespace-pre-wrap font-sans text-muted-foreground bg-secondary/40 rounded-lg p-3">{extra[o.opportunity_id].customers}</pre></div>
                  )}
                  <p className="text-[11px] text-muted-foreground font-mono">Fonte: {o.source} · Estimativas, não garantia de lucro.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
